import {
  httpFetch,
  httpFetchJson,
} from '../../../../core/services/api/httpClient';
import { UploadChunkRequestBody } from '../../../../core/models/fileUpload/uploadChunkRequestBody.ts';
import { TranslatableLanguageValueEnum } from '../../enums/translatableLanguageValueEnum.ts';
import { UploadInitRequestBody } from '../../../../core/models/fileUpload/uploadInitRequestBody.ts';
import {
  TranslationUploadInitResponse,
  TranslationUploadChunkResponse,
  TranslationStartResponse,
  TranslationStatusResponse,
} from '../../../../core/models/fileUpload/translationResponses.ts';
import {
  LastUsedLanguageResponseModel,
  TranslatableLanguageModel,
} from '../../types.ts';

const chunkSize = (1024 * 1024) / 2; // 500KB per chunk

type TranslationDownloadResponse = {
  data: Blob;
  headers: Record<string, string>;
};

const getTranslationOptionsAsync = async (): Promise<
  TranslatableLanguageModel[]
> => {
  return httpFetchJson<TranslatableLanguageModel[]>(
    '/api/documentTranslation/languages/cached',
    { withBackendAuth: true }
  );
};

const getLastUsedLanguageAsync =
  async (): Promise<LastUsedLanguageResponseModel> => {
    return httpFetchJson<LastUsedLanguageResponseModel>(
      '/api/documentTranslation/lastUsedLanguage',
      { withBackendAuth: true }
    );
  };

const initiateUpdateDocumentToTranslateAsync = async (
  requestBody: UploadInitRequestBody
): Promise<TranslationUploadInitResponse> => {
  return httpFetchJson<TranslationUploadInitResponse>(
    '/api/documentTranslation/upload/init',
    {
      method: 'POST',
      withBackendAuth: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: requestBody.file.name,
        total_chunks: Math.ceil(requestBody.file.size / chunkSize),
      }),
    }
  );
};

const updateDocumentToTranslateChunkAsync = async (
  requestBody: UploadChunkRequestBody
): Promise<TranslationUploadChunkResponse> => {
  const params = new URLSearchParams({
    chunk_number: String(requestBody.chunk_number),
  });
  return httpFetchJson<TranslationUploadChunkResponse>(
    `/api/documentTranslation/upload/chunk/${requestBody.translation_id}?${params}`,
    {
      method: 'POST',
      withBackendAuth: true,
      body: requestBody.form_data,
    }
  );
};

const startDocumentTranslationAsync = async (
  translation_id: string,
  target_language: TranslatableLanguageValueEnum
): Promise<TranslationStartResponse> => {
  const params = new URLSearchParams({ target_language });
  return httpFetchJson<TranslationStartResponse>(
    `/api/documentTranslation/start_async_translate/${translation_id}?${params}`,
    { withBackendAuth: true }
  );
};

const checkTranslationStatusAsync = async (
  translation_id: string
): Promise<TranslationStatusResponse> => {
  return httpFetchJson<TranslationStatusResponse>(
    `/api/documentTranslation/status/${translation_id}`,
    { withBackendAuth: true }
  );
};

const downloadTranslatedDocumentAsync = async (
  translation_id: string
): Promise<TranslationDownloadResponse> => {
  const response = await httpFetch(
    `/api/documentTranslation/download/${translation_id}`,
    { withBackendAuth: true }
  );

  const headers: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headers[key] = value;
  });

  return {
    data: await response.blob(),
    headers,
  };
};

export const documentTranslationApi = {
  chunkSize,
  getTranslationOptionsAsync,
  getLastUsedLanguageAsync,
  initiateUpdateDocumentToTranslateAsync,
  updateDocumentToTranslateChunkAsync,
  startDocumentTranslationAsync,
  checkTranslationStatusAsync,
  downloadTranslatedDocumentAsync,
};
